import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Check, Pencil, Plus, RotateCcw, Sparkles, Trash2, X } from 'lucide-react';
import { ConstructionType } from '../../types';
import { Button } from '../Button';
import {
    ProjectCategoryDef, countMissingDefaults, mergeMissingDefaults, normalizeCategoryDefs,
} from '../../services/projectCategories';

/**
 * Verwaltung der Projekt-Kategorisierung im Firmenbereich.
 *
 * Zwei Ebenen, die im Projekt getrennt abgefragt werden:
 *  · Kategorie → Unterkategorie (Pflichtfeld der Stammdaten, Referenzblatt,
 *    Datenpflege-Dialog). Bis 2026-09 fest im Code, jetzt hier pflegbar.
 *  · Bauart (constructionTypes): freie Zusatzliste mit Beschreibung,
 *    erscheint als eigenes Feld unter der Kategorie.
 *
 * Die Komponente haelt keinen eigenen Katalogstand: jede Aenderung geht als
 * ganze Liste an den Aufrufer (Autosave in AdminGlobalSettings). Beim ersten
 * Eingriff in den Standardkatalog wird er dadurch als eigener Katalog ins
 * Profil geschrieben; „Auf Standard zuruecksetzen" loescht ihn wieder.
 */
interface Props {
    categories: ProjectCategoryDef[];
    isCustom: boolean;
    onChangeCategories: (list: ProjectCategoryDef[] | undefined) => void;
    constructionTypes: ConstructionType[];
    onChangeConstructionTypes: (list: ConstructionType[]) => void;
}

const feld = 'border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 bg-white';

export const ProjectCategoryManager: React.FC<Props> = ({
    categories, isCustom, onChangeCategories, constructionTypes, onChangeConstructionTypes,
}) => {
    const [neueKategorie, setNeueKategorie] = useState('');
    const [neueSub, setNeueSub] = useState<Record<string, string>>({});
    const [titelBearbeitung, setTitelBearbeitung] = useState<{ index: number; wert: string } | null>(null);
    const [neueBauart, setNeueBauart] = useState({ title: '', description: '' });
    const [bauartBearbeitung, setBauartBearbeitung] = useState<ConstructionType | null>(null);

    const subGesamt = categories.reduce((n, c) => n + c.subs.length, 0);
    const fehlend = countMissingDefaults(categories);
    const fehlendGesamt = fehlend.categories + fehlend.subs;

    const schreibe = (next: ProjectCategoryDef[]) => onChangeCategories(normalizeCategoryDefs(next));

    const kategorieHinzufuegen = () => {
        const t = neueKategorie.trim();
        if (!t) return;
        if (categories.some(c => c.title === t)) { setNeueKategorie(''); return; }
        schreibe([...categories, { title: t, subs: [] }]);
        setNeueKategorie('');
    };

    const kategorieEntfernen = (index: number) => {
        const c = categories[index];
        const hinweis = c.subs.length > 0
            ? `Kategorie „${c.title}" mit ${c.subs.length} Unterkategorien entfernen?\n\nBestehende Projekte behalten ihren Wert, er ist danach nur nicht mehr auswählbar.`
            : `Kategorie „${c.title}" entfernen?`;
        if (!window.confirm(hinweis)) return;
        schreibe(categories.filter((_, i) => i !== index));
    };

    const kategorieVerschieben = (index: number, richtung: -1 | 1) => {
        const ziel = index + richtung;
        if (ziel < 0 || ziel >= categories.length) return;
        const next = [...categories];
        [next[index], next[ziel]] = [next[ziel], next[index]];
        schreibe(next);
    };

    const titelUebernehmen = () => {
        if (!titelBearbeitung) return;
        const t = titelBearbeitung.wert.trim();
        const alt = categories[titelBearbeitung.index];
        if (t && alt && t !== alt.title && !categories.some(c => c.title === t)) {
            schreibe(categories.map((c, i) => (i === titelBearbeitung.index ? { ...c, title: t } : c)));
        }
        setTitelBearbeitung(null);
    };

    const subHinzufuegen = (index: number) => {
        const s = (neueSub[categories[index].title] || '').trim();
        if (!s) return;
        schreibe(categories.map((c, i) => (i === index && !c.subs.includes(s) ? { ...c, subs: [...c.subs, s] } : c)));
        setNeueSub(prev => ({ ...prev, [categories[index].title]: '' }));
    };

    const subEntfernen = (index: number, sub: string) => {
        schreibe(categories.map((c, i) => (i === index ? { ...c, subs: c.subs.filter(x => x !== sub) } : c)));
    };

    const standardErgaenzen = () => schreibe(mergeMissingDefaults(categories));

    const standardZuruecksetzen = () => {
        if (!window.confirm('Eigenen Katalog verwerfen und den eingebauten Standardkatalog verwenden?\n\nEigene Kategorien und Unterkategorien gehen dabei verloren.')) return;
        onChangeCategories(undefined);
    };

    // ── Bauart ──────────────────────────────────────────────────────────────
    const bauartHinzufuegen = () => {
        const title = neueBauart.title.trim();
        if (!title) return;
        onChangeConstructionTypes([
            ...constructionTypes,
            { id: crypto.randomUUID(), title, description: neueBauart.description.trim() },
        ]);
        setNeueBauart({ title: '', description: '' });
    };

    const bauartEntfernen = (id: string) => {
        const t = constructionTypes.find(x => x.id === id);
        if (!window.confirm(`Bauart „${t?.title || ''}" entfernen?`)) return;
        onChangeConstructionTypes(constructionTypes.filter(x => x.id !== id));
    };

    const bauartUebernehmen = () => {
        if (!bauartBearbeitung) return;
        const title = bauartBearbeitung.title.trim();
        if (title) {
            onChangeConstructionTypes(constructionTypes.map(x => (
                x.id === bauartBearbeitung.id ? { ...x, title, description: bauartBearbeitung.description.trim() } : x
            )));
        }
        setBauartBearbeitung(null);
    };

    return (
        <div className="space-y-6 mt-4">
            {/* ── Kategorien ── */}
            <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="text-sm font-bold text-slate-800">Kategorien &amp; Unterkategorien</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            {categories.length} Kategorien · {subGesamt} Unterkategorien ·{' '}
                            <span className={isCustom ? 'text-purple-700 font-semibold' : 'text-slate-500'}>
                                {isCustom ? 'eigener Katalog' : 'Standardkatalog'}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="secondary" size="sm" onClick={standardErgaenzen} disabled={fehlendGesamt === 0}
                            icon={<Sparkles className="w-3.5 h-3.5" />}
                            title={fehlendGesamt === 0 ? 'Alle Standardeinträge sind vorhanden' : `${fehlend.categories} Kategorien, ${fehlend.subs} Unterkategorien fehlen`}
                        >
                            Standard ergänzen{fehlendGesamt > 0 ? ` (+${fehlendGesamt})` : ''}
                        </Button>
                        {isCustom && (
                            <Button variant="ghost" size="sm" onClick={standardZuruecksetzen} icon={<RotateCcw className="w-3.5 h-3.5" />}>
                                Auf Standard zurücksetzen
                            </Button>
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    {categories.map((c, index) => {
                        const bearbeitet = titelBearbeitung?.index === index;
                        return (
                            <div key={c.title} className="border border-slate-200 rounded-xl bg-white shadow-sm">
                                <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/70 rounded-t-xl">
                                    {bearbeitet ? (
                                        <>
                                            <input
                                                autoFocus type="text" className={`${feld} flex-1 py-1`} value={titelBearbeitung.wert}
                                                onChange={e => setTitelBearbeitung({ index, wert: e.target.value })}
                                                onKeyDown={e => { if (e.key === 'Enter') titelUebernehmen(); if (e.key === 'Escape') setTitelBearbeitung(null); }}
                                                aria-label="Kategorie umbenennen"
                                            />
                                            <button type="button" onClick={titelUebernehmen} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md" title="Übernehmen"><Check className="w-4 h-4" /></button>
                                            <button type="button" onClick={() => setTitelBearbeitung(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-md" title="Abbrechen"><X className="w-4 h-4" /></button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex-1 font-bold text-slate-800 text-sm">{c.title}</div>
                                            <span className="text-[11px] text-slate-400 mr-1">{c.subs.length}</span>
                                            <button type="button" onClick={() => setTitelBearbeitung({ index, wert: c.title })} className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-md" title="Umbenennen"><Pencil className="w-3.5 h-3.5" /></button>
                                            <button type="button" onClick={() => kategorieVerschieben(index, -1)} disabled={index === 0} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-30" title="Nach oben"><ArrowUp className="w-3.5 h-3.5" /></button>
                                            <button type="button" onClick={() => kategorieVerschieben(index, 1)} disabled={index === categories.length - 1} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-30" title="Nach unten"><ArrowDown className="w-3.5 h-3.5" /></button>
                                            <button type="button" onClick={() => kategorieEntfernen(index)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-md" title="Kategorie entfernen"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </>
                                    )}
                                </div>
                                <div className="px-3 py-2 flex flex-wrap items-center gap-1.5">
                                    {c.subs.length === 0 && <span className="text-xs text-slate-400 italic">Noch keine Unterkategorie</span>}
                                    {c.subs.map(sub => (
                                        <span key={sub} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs rounded-full pl-2.5 pr-1 py-0.5 border border-slate-200">
                                            {sub}
                                            <button type="button" onClick={() => subEntfernen(index, sub)} className="p-0.5 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50" title={`„${sub}" entfernen`}><X className="w-3 h-3" /></button>
                                        </span>
                                    ))}
                                    <span className="inline-flex items-center gap-1 ml-auto">
                                        <input
                                            type="text" placeholder="Unterkategorie hinzufügen…"
                                            className={`${feld} py-1 text-xs w-52`}
                                            value={neueSub[c.title] || ''}
                                            onChange={e => setNeueSub(prev => ({ ...prev, [c.title]: e.target.value }))}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); subHinzufuegen(index); } }}
                                            aria-label={`Unterkategorie zu ${c.title} hinzufügen`}
                                        />
                                        <button type="button" onClick={() => subHinzufuegen(index)} disabled={!(neueSub[c.title] || '').trim()} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-md disabled:opacity-30" title="Hinzufügen"><Plus className="w-4 h-4" /></button>
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex gap-2 items-center bg-slate-50 p-3 rounded-xl border border-slate-200">
                    <input
                        type="text" className={`${feld} flex-1`} placeholder="Neue Kategorie, z. B. Wasser & Umwelt" value={neueKategorie}
                        onChange={e => setNeueKategorie(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); kategorieHinzufuegen(); } }}
                        aria-label="Neue Kategorie"
                    />
                    <Button variant="secondary" onClick={kategorieHinzufuegen} disabled={!neueKategorie.trim()} className="bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200" icon={<Plus className="w-4 h-4" />}>Kategorie</Button>
                </div>
            </div>

            {/* ── Bauart ── */}
            <div className="space-y-3 pt-4 border-t border-slate-100">
                <div>
                    <div className="text-sm font-bold text-slate-800">Bauart</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                        {constructionTypes.length} {constructionTypes.length === 1 ? 'Bauart' : 'Bauarten'} · freie Zusatzangabe zur Kategorie, eigenes Feld in den Projekt-Stammdaten
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-3 items-end bg-slate-50 p-3 rounded-xl border border-slate-200">
                    <div className="flex-1 w-full"><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Bezeichnung</label><input type="text" className={`${feld} w-full`} placeholder="z. B. Massivbau" value={neueBauart.title} onChange={e => setNeueBauart({ ...neueBauart, title: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); bauartHinzufuegen(); } }} /></div>
                    <div className="flex-[2] w-full"><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Beschreibung / Umfang</label><input type="text" className={`${feld} w-full`} placeholder="Stahlbeton, Mauerwerk, …" value={neueBauart.description} onChange={e => setNeueBauart({ ...neueBauart, description: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); bauartHinzufuegen(); } }} /></div>
                    <Button variant="secondary" onClick={bauartHinzufuegen} disabled={!neueBauart.title.trim()} className="bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200 h-[38px]" icon={<Plus className="w-4 h-4" />}>Hinzufügen</Button>
                </div>
                {constructionTypes.length === 0 ? (
                    <p className="text-xs text-slate-400 italic px-1">Noch keine Bauart angelegt. Ohne Einträge bleibt das Feld im Projekt ausgeblendet.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {constructionTypes.map(t => {
                            const b = bauartBearbeitung?.id === t.id ? bauartBearbeitung : null;
                            return (
                                <div key={t.id} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:border-purple-200 transition-colors">
                                    {b ? (
                                        <div className="space-y-2">
                                            <input autoFocus type="text" className={`${feld} w-full py-1`} value={b.title} onChange={e => setBauartBearbeitung({ ...b, title: e.target.value })} onKeyDown={e => { if (e.key === 'Escape') setBauartBearbeitung(null); }} aria-label="Bezeichnung" />
                                            <textarea rows={2} className={`${feld} w-full py-1 resize-none`} value={b.description} onChange={e => setBauartBearbeitung({ ...b, description: e.target.value })} aria-label="Beschreibung" />
                                            <div className="flex justify-end gap-1">
                                                <button type="button" onClick={() => setBauartBearbeitung(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-md" title="Abbrechen"><X className="w-4 h-4" /></button>
                                                <button type="button" onClick={bauartUebernehmen} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md" title="Übernehmen"><Check className="w-4 h-4" /></button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="min-w-0">
                                                <div className="font-bold text-slate-700 text-sm">{t.title}</div>
                                                {t.description && <div className="text-xs text-slate-500 leading-snug mt-1 whitespace-pre-line">{t.description}</div>}
                                            </div>
                                            <div className="flex shrink-0">
                                                <button type="button" onClick={() => setBauartBearbeitung({ ...t })} className="p-1 text-slate-300 hover:text-purple-600" title="Bearbeiten"><Pencil className="w-4 h-4" /></button>
                                                <button type="button" onClick={() => bauartEntfernen(t.id)} className="p-1 text-slate-300 hover:text-red-500" title="Entfernen"><Trash2 className="w-4 h-4" /></button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
